import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import BackspaceRoundedIcon from "@mui/icons-material/BackspaceRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";
import { useAlert } from "../../../../context/AlertContext";

const keypad = [
  "7",
  "8",
  "9",
  "/",
  "4",
  "5",
  "6",
  "*",
  "1",
  "2",
  "3",
  "-",
  "0",
  ".",
  "=",
  "+",
];

const ivaOptions = [0, 5, 15];

function normalizeDecimal(value) {
  let text = String(value ?? "");
  text = text.replace(/,/g, ".");
  text = text.replace(/[^\d.]/g, "");
  const firstDot = text.indexOf(".");

  if (firstDot !== -1) {
    text = text.slice(0, firstDot + 1) + text.slice(firstDot + 1).replace(/\./g, "");
  }

  return text;
}

function normalizeExpression(value) {
  let text = String(value ?? "");
  text = text.replace(/,/g, ".");
  text = text.replace(/×/g, "*");
  text = text.replace(/÷/g, "/");
  text = text.replace(/[^0-9+\-*/().% ]/g, "");
  return text;
}

function toNumber(value) {
  const number = Number(normalizeDecimal(value));
  return Number.isFinite(number) ? number : 0;
}

function round6(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toFixed(6) : "0.000000";
}

function getFactor(iva) {
  return 1 + Number(iva || 0) / 100;
}

function safeCalculate(expression) {
  const clean = normalizeExpression(expression);

  if (!clean.trim()) return "";
  if (!/^[0-9+\-*/().% ]+$/.test(clean)) return "Error";

  try {
    const result = Function(`"use strict"; return (${clean})`)();

    if (!Number.isFinite(result)) return "Error";

    return String(Number(result.toFixed(12)));
  } catch {
    return "Error";
  }
}

export default function Calculator() {
  const { dashboardTheme } = useDashboardTheme();
  const { showSuccess, showError } = useAlert();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("price");
  const [finalPrice, setFinalPrice] = useState("");
  const [iva, setIva] = useState(15);
  const [expression, setExpression] = useState("");
  const [normalResult, setNormalResult] = useState("");

  const factor = getFactor(iva);

  const priceResult = useMemo(() => {
    const total = toNumber(finalPrice);
    const pvp = factor === 0 ? 0 : total / factor;

    return {
      pvp6: round6(pvp),
    };
  }, [finalPrice, factor]);

  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      showSuccess("PVP copiado.");
    } catch {
      showError("No se pudo copiar.");
    }
  };

  const pressKey = (key) => {
    if (key === "=") {
      setNormalResult(safeCalculate(expression));
      return;
    }

    setExpression((prev) => normalizeExpression(`${prev}${key}`));
  };

  const clearNormal = () => {
    setExpression("");
    setNormalResult("");
  };

  const backspaceNormal = () => {
    setExpression((prev) => prev.slice(0, -1));
  };

  const outlinedButtonSx = {
    minHeight: 38,
    px: 1.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    bgcolor: dashboardTheme.card_background,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: dashboardTheme.dashboard_background,
      boxShadow: "none",
    },
  };

  const activeButtonSx = {
    minHeight: 38,
    px: 1.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.button_color,
      opacity: 0.92,
      boxShadow: "none",
    },
  };

  const keypadButtonSx = {
    minWidth: 0,
    height: 38,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.text_color,
    borderColor: dashboardTheme.border_color,
    bgcolor: dashboardTheme.card_background,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.dashboard_background,
      borderColor: dashboardTheme.border_color,
      boxShadow: "none",
    },
  };

  const copyButtonSx = {
    color: dashboardTheme.text_color,
    border: `1px solid ${dashboardTheme.border_color}`,
    borderRadius: "10px",
    width: 36,
    height: 36,
    bgcolor: dashboardTheme.card_background,
    boxShadow: "none",
    "&:hover": {
      bgcolor: dashboardTheme.dashboard_background,
      boxShadow: "none",
    },
  };

  return (
    <Box
      sx={{
        position: "fixed",
        right: { xs: 16, md: 24 },
        bottom: { xs: 16, md: 24 },
        zIndex: 1400,
      }}
    >
      {open && (
        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            right: 0,
            bottom: 72,
            width: { xs: 320, sm: 410 },
            maxWidth: "calc(100vw - 32px)",
            borderRadius: "16px",
            border: `1px solid ${dashboardTheme.border_color}`,
            bgcolor: dashboardTheme.card_background,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.4,
              bgcolor: dashboardTheme.primary_color,
              color: dashboardTheme.button_text_color,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 700,
              }}
            >
              Calculadora
            </Typography>

            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: dashboardTheme.button_text_color,
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Stack spacing={1.6} sx={{ p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              <Button
                variant={mode === "price" ? "contained" : "outlined"}
                onClick={() => setMode("price")}
                sx={mode === "price" ? activeButtonSx : outlinedButtonSx}
              >
                Precio final
              </Button>

              <Button
                variant={mode === "normal" ? "contained" : "outlined"}
                onClick={() => setMode("normal")}
                sx={mode === "normal" ? activeButtonSx : outlinedButtonSx}
              >
                Normal
              </Button>
            </Box>

            {mode === "price" ? (
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Precio final"
                  value={finalPrice}
                  onChange={(event) => setFinalPrice(normalizeDecimal(event.target.value))}
                  sx={dashboardTheme.inputStyles}
                  inputProps={{
                    inputMode: "decimal",
                  }}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1,
                  }}
                >
                  {ivaOptions.map((item) => (
                    <Button
                      key={item}
                      variant={iva === item ? "contained" : "outlined"}
                      onClick={() => setIva(item)}
                      sx={iva === item ? activeButtonSx : outlinedButtonSx}
                    >
                      IVA {item}%
                    </Button>
                  ))}
                </Box>

                <Box
                  sx={{
                    borderRadius: "14px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    bgcolor: dashboardTheme.dashboard_background,
                    p: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: dashboardTheme.muted_text_color,
                    }}
                  >
                    PVP
                  </Typography>

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography
                      sx={{
                        mt: 0.3,
                        fontSize: 24,
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                        lineHeight: 1.2,
                      }}
                    >
                      ${priceResult.pvp6}
                    </Typography>

                    <IconButton
                      onClick={() => copyValue(priceResult.pvp6)}
                      sx={copyButtonSx}
                    >
                      <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Operación"
                  value={expression}
                  onChange={(event) => setExpression(normalizeExpression(event.target.value))}
                  sx={dashboardTheme.inputStyles}
                  inputProps={{
                    inputMode: "decimal",
                  }}
                />

                <Box
                  sx={{
                    borderRadius: "14px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    bgcolor: dashboardTheme.dashboard_background,
                    p: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: dashboardTheme.muted_text_color,
                    }}
                  >
                    Resultado
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.3,
                      fontSize: 24,
                      fontWeight: 700,
                      color: dashboardTheme.text_color,
                      lineHeight: 1.2,
                    }}
                  >
                    {normalResult || "0"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 0.8,
                  }}
                >
                  {keypad.map((key) => (
                    <Button
                      key={key}
                      variant="outlined"
                      onClick={() => pressKey(key)}
                      sx={
                        key === "="
                          ? {
                              ...activeButtonSx,
                              minWidth: 0,
                              height: 38,
                              px: 0,
                            }
                          : keypadButtonSx
                      }
                    >
                      {key === "*" ? "×" : key === "/" ? "÷" : key}
                    </Button>
                  ))}
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={clearNormal}
                    sx={outlinedButtonSx}
                  >
                    Limpiar
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<BackspaceRoundedIcon />}
                    onClick={backspaceNormal}
                    sx={outlinedButtonSx}
                  >
                    Borrar
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      <Fab
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          width: 56,
          height: 56,
          bgcolor: dashboardTheme.button_color,
          color: dashboardTheme.button_text_color,
          boxShadow: "none",
          border: `1px solid ${dashboardTheme.border_color}`,
          "&:hover": {
            bgcolor: dashboardTheme.button_color,
            opacity: 0.92,
            boxShadow: "none",
          },
        }}
      >
        {open ? <CloseRoundedIcon /> : <CalculateRoundedIcon />}
      </Fab>
    </Box>
  );
}